<template>
  <teleport to="body">
    <div :class="['modal-overlay', 'child-modal', { show: show }]" v-show="show" @click.self="handleClickAway">
      <div class="modal-container child-modal-container" :class="{ 'modal-enter-active': show }">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header d-flex justify-content-between">
            <div class="modal-title-section">
              <div class="modal-title-content">
                <h2 class="modal-title text-light">
                  <i class="bi bi-info"></i> {{ title }}
                </h2>
              </div>
            </div>
            <button v-if="closeable" @click="close" type="button" class="close-btn" aria-label="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="modal-body">
            <div class="body-content">
              <slot />
            </div>
          </div>

          <!-- Modal Footer -->
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  name: "ChildModal",
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: "Details",
    },
    subtitle: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "bi-info-circle-fill",
    },
    iconClass: {
      type: String,
      default: "info",
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    footerText: {
      type: String,
      default: "Quick Actions",
    },
    footerIcon: {
      type: String,
      default: "bi-lightning-fill",
    },
    clickAway: {
      type: Boolean,
      default: true
    },
    closeable: {
      type: Boolean,
      default: true
    }
  },
  emits: ["close", "confirm"],
  setup(props, { emit }) {
    const close = () => {
      if (props.closeable)
        emit("close");
    };
    const handleClickAway = ()=>{
      if(props.clickAway)
        close();
    }

    const confirm = () => {
      emit("confirm");
    };

    return {
      close,
      confirm,
      handleClickAway,
    };
  },
};
</script>

<style scoped>
/* Child Modal Specific Styles */
.modal-overlay.child-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 9999;
  /* Ultra high z-index */
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-overlay.child-modal.show {
  opacity: 1;
  visibility: visible;
}

.modal-container.child-modal-container {
  position: fixed;
  top: 50%;
  left: 50%;
  width: 90%;
  max-width: 600px;
  height: auto;
  max-height: 80vh;
  transform: translate(-50%, -50%) scale(0.9);
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1),
    0 0 80px rgba(163, 14, 14, 0.2);
  z-index: 10000;
  /* Even higher than overlay */
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-container.child-modal-container.modal-enter-active {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
}

/* Ensure no parent can override positioning */
.modal-overlay.child-modal,
.modal-container.child-modal-container {
  position: fixed !important;
  z-index: 9999 !important;
}

.modal-overlay.child-modal .modal-header {
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  border-radius: 16px 16px 0 0;
}

.modal-overlay.child-modal .title-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  animation: none;
}

.modal-overlay.child-modal .title-icon i {
  font-size: 1.25rem;
}

.modal-overlay.child-modal .modal-title {
  font-size: 1.25rem;
}

.modal-overlay.child-modal .modal-subtitle {
  font-size: 0.8rem;
}

.modal-overlay.child-modal .modal-body {
  max-height: 50vh;
  overflow-y: auto;
  padding: 0;
}

.modal-overlay.child-modal .body-content {
  padding: 1.5rem;
}

.modal-overlay.child-modal .modal-footer {
  padding: 1rem 1.5rem;
}

.modal-overlay.child-modal .footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.modal-overlay.child-modal .action-buttons {
  display: flex;
  gap: 0.75rem;
}

/* Button Styles */
.btn {
  padding: 0.625rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: linear-gradient(135deg, #a30e0e 0%, #8a0b0b 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(163, 14, 14, 0.3);
  background: linear-gradient(135deg, #8a0b0b 0%, #700000 100%);
}

.btn-secondary {
  background: linear-gradient(135deg, #6c757d, #5a6268);
  color: white;
}

.btn-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
  background: linear-gradient(135deg, #5a6268, #495057);
}

.btn:active {
  transform: translateY(0);
}

/* Icon Variants */
.title-icon.info {
  background: rgba(74, 144, 226, 0.2);
  border: 1px solid rgba(74, 144, 226, 0.3);
}

.title-icon.info i {
  color: #4a90e2;
}

.title-icon.warning {
  background: rgba(255, 193, 7, 0.2);
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.title-icon.warning i {
  color: #ffc107;
}

.title-icon.success {
  background: rgba(40, 167, 69, 0.2);
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.title-icon.success i {
  color: #28a745;
}

.title-icon.danger {
  background: rgba(163, 14, 14, 0.2);
  border: 1px solid rgba(163, 14, 14, 0.3);
}

.title-icon.danger i {
  color: #a30e0e;
}

/* Enhanced focus states */
.btn:focus {
  outline: 2px solid rgba(163, 14, 14, 0.5);
  outline-offset: 2px;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1) rotate(90deg);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.close-btn:active {
  transform: scale(0.95);
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-container.child-modal-container {
    width: 95%;
    max-width: none;
    margin: 1rem;
    border-radius: 12px;
  }

  .modal-overlay.child-modal .footer-actions {
    flex-direction: column;
    gap: 1rem;
  }

  .modal-overlay.child-modal .action-buttons {
    width: 100%;
    justify-content: stretch;
  }

  .modal-overlay.child-modal .action-buttons .btn {
    flex: 1;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .modal-overlay.child-modal .modal-header {
    padding: 1rem;
  }

  .modal-overlay.child-modal .body-content {
    padding: 1rem;
  }

  .modal-overlay.child-modal .modal-footer {
    padding: 0.875rem 1rem;
  }

  .btn {
    padding: 0.75rem 1rem;
    font-size: 0.8rem;
  }
}

/* Smooth backdrop for nested modals */
.modal-overlay.child-modal::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center,
      rgba(163, 14, 14, 0.05) 0%,
      transparent 70%);
  pointer-events: none;
}

/* Ensure no scroll on body when modal is open */
body.modal-open {
  overflow: hidden;
}
</style>